// ============================================================
// Landing Page Component
// Fully open — no gates, no email needed to browse.
// The OTP/payment gates only appear when the user chooses to
// actually start the free trial or the paid assessment.
// ============================================================

import React, { useState } from 'react';

// v1.2.1 follow-up: two-screen redesign so the page reads as an app
// screen, not a scrolling website. Hero stays its own screen with
// both CTAs (price now shown upfront, see rationale discussed with
// Hardy); the 5 explanatory sections below move behind a deliberate
// "Learn more" tap rather than continuous scroll, each still showing
// its full, unchanged original content once tapped into.
const TILES = [
  { key: 'chart', title: 'What is C-H-A-R-T?', sub: 'The 5 dimensions this assessment measures' },
  { key: 'hardy', title: 'Why CG Hardy', sub: "Built by someone who's lived it" },
  { key: 'helps', title: 'How This Helps You', sub: 'What you actually walk away with' },
  { key: 'report', title: 'Free vs. Full Report', sub: "What's included at each level" },
  { key: 'gcc', title: 'Understanding the GCC Opportunity (FAQ)', sub: 'Common questions about the shift, answered' }
];

function Landing({ onStartTrial, onStartPaid, onShowPrivacy, onShowTerms, onShowRefund, onShowContact }) {
  const [view, setView] = useState('hero'); // 'hero' | 'tiles' | one of TILES[].key

  return (
    <div className="landing">

      {/* Slim app-style top bar — replaces the old large centered
          logo block, present on every screen for consistent identity
          without repeating vertical space on each one. */}
      <div className="landing-topbar">
        <img src="/images/chart-badge.png" alt="" className="topbar-logo-icon" />
        <span className="topbar-wordmark">CHART Rubric</span>
      </div>

      {/* HERO — its own screen */}
      {view === 'hero' && (
      <section className="hero">
        <p className="eyebrow-tag">GLOBAL CAPABILITY CENTRE (GCC) READINESS ASSESSMENT &amp; DEVELOPMENT PLAN</p>
        <h1>Discover how ready you are for the next generation of GCC careers.</h1>
        <p className="sub-headline">Know how you really think, decide, and work under pressure.</p>
        <p className="hero-sub">CHART Rubric is a behavioral readiness assessment and development plan built for GCC aspirants and professionals. In a world where AI can do more of the routine work, how you handle judgment, ambiguity, and people is what actually sets you apart.</p>

        <div className="receive-box">
          <div className="receive-box-header">
            <div className="receive-title">What You'll Receive</div>
            <button className="learn-more-inline" onClick={() => setView('tiles')}>Learn more about CHART →</button>
          </div>
          <ul className="receive-list">
            <li>Personalized CHART Score</li>
            <li>Five-dimension behavioural profile, from 20 real scenarios</li>
            <li>Strengths &amp; blind spots</li>
            <li>Development recommendations — a full 180-day plan, downloadable as PDF</li>
            <li>GCC readiness insights</li>
          </ul>
        </div>

        {/* Both CTAs now live here, price shown upfront — matches
            the approved mockups. The original design delayed the
            paid CTA until after the C-H-A-R-T explanation; that
            reasoning assumed passive scroll exposure, which no
            longer applies once that content sits behind a
            deliberate tap. Confirmed with Hardy before changing. */}
        <div className="hero-actions">
          <button className="btn-secondary" onClick={onStartTrial}>Try 3 Free Questions</button>
          <button className="btn-primary" onClick={onStartPaid}>Get Full Report — ₹999</button>
          <div className="launch-note">Launch price — limited slots</div>
        </div>

        <div className="experience-note">
          <p>CHART Rubric works best for those with 4+ years of experience. If you're earlier in your career, this alternative might suit you better.</p>
          <a href="https://www.sarthiedge.com/sarthi-professionals" target="_blank" rel="noopener noreferrer">
            <button type="button" className="btn-secondary experience-redirect-btn">Explore Sarthi Professionals</button>
          </a>
        </div>
      </section>
      )}

      {/* TILES — the new "Learn More" screen, reached by tap */}
      {view === 'tiles' && (
      <section className="tiles-screen">
        <button className="back-link" onClick={() => setView('hero')}>← Back</button>
        <div className="tiles-grid">
          {TILES.map(t => (
            <div key={t.key} className={`tile${t.key === 'gcc' ? ' tile-wide' : ''}`} onClick={() => setView(t.key)}>
              <div className="tile-title">{t.title}</div>
              <div className="tile-sub">{t.sub}</div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* WHAT IS CHART */}
      {view === 'chart' && (
      <section className="content-section detail-screen">
        <button className="back-link" onClick={() => setView('tiles')}>← Back to Learn More</button>
        <h2>What is C-H-A-R-T?</h2>
        <p className="section-lead">Five human capabilities. One clear picture of where you stand.</p>

        {/* Simple framework diagram - no photos, just structured visual */}
        <div className="chart-diagram">
          {[
            { letter: 'C', label: 'Creativity' },
            { letter: 'H', label: 'Human Judgment' },
            { letter: 'A', label: 'Adaptability' },
            { letter: 'R', label: 'Relationship Intelligence' },
            { letter: 'T', label: 'Trust' }
          ].map(dim => (
            <div key={dim.letter} className="chart-diagram-segment">
              <div className="chart-diagram-letter">{dim.letter}</div>
              <div className="chart-diagram-label">{dim.label}</div>
            </div>
          ))}
        </div>

        <ul className="chart-list">
          <li><strong>C — Creativity</strong> — Do you just spot problems, or actually build and prove better solutions?</li>
          <li><strong>H — Human Judgment</strong> — When data, hierarchy, or automation says one thing and your gut says another — what do you do?</li>
          <li><strong>A — Adaptability</strong> — When the ground shifts, do you freeze, comply quietly, or actually lead through it?</li>
          <li><strong>R — Relationship Intelligence</strong> — Can you build real trust and influence across teams and cultures, without needing formal authority?</li>
          <li><strong>T — Trust</strong> — Do people get the real picture from you, even when it's uncomfortable?</li>
        </ul>
        <p className="section-footnote">Scored from your actual answers to real workplace scenarios. Not self-ratings. Not guesswork.</p>

        {/* Paid CTA relocated here — after the framework explanation, not
            immediately in the hero. The free trial button remains in the
            hero as the low-commitment option; this paid button appears
            once the visitor has actually learned what CHART measures. */}
        <div className="paid-cta-block">
          <p className="credibility-line">Built on 25+ years of experience in talent, workforce strategy and enterprise transformation.</p>
          <button className="btn-primary" onClick={onStartPaid}>Launch Offer — ₹999 (first 100)</button>
          <div className="price-subtext">Regular Price ₹1,499 • Limited to the first 100 professionals</div>
          <p className="secure-payment-note">🔒 Pay safely and securely using Razorpay</p>
        </div>
      </section>
      )}

      {/* WHY CHART */}
      {view === 'hardy' && (
      <section className="content-section alt-bg detail-screen">
        <button className="back-link" onClick={() => setView('tiles')}>← Back to Learn More</button>
        <h2>Why CHART? Built by CG Hardy — someone who's lived it.</h2>
        <p className="section-lead">25+ years across the service industry and product companies, working closely with GCC clients throughout.</p>
        <p>Built from 25+ years across the service industry and product companies, working closely with GCC clients throughout that time. I've lived through every stage of India's Global Delivery Model as it evolved — from IT Services in the 1990s, through Shared Services, into Global In-house Centers, then today's Global Capability Centers, and now the early shift toward AI-Native Capability Centers. That evolution isn't something I read about — I was working alongside the organizations going through each shift, as it happened.</p>
        <p>Most tools measure what you know. CHART measures how you actually behave when things get hard — because that's what separates people who get promoted from people who get replaced, by AI or by someone else.</p>
        <p className="section-signature">— CG Hardy, Founder</p>
      </section>
      )}

      {/* HOW IT HELPS */}
      {view === 'helps' && (
      <section className="content-section detail-screen">
        <button className="back-link" onClick={() => setView('tiles')}>← Back to Learn More</button>
        <h2>How This Helps You</h2>
        <div className="two-col">
          <div className="col-card">
            <h3>Trying to get INTO a GCC?</h3>
            <p>Walk into interviews already knowing your real strengths and gaps — not generic buzzwords.</p>
          </div>
          <div className="col-card">
            <h3>Already IN a GCC?</h3>
            <p>Get an honest read on where you're strong and where you're quietly vulnerable — before it becomes a difficult conversation.</p>
          </div>
        </div>
        <p className="section-footnote">Every report is tailored to your career stage (Individual Contributor, Manager, Director, or VP) and your industry (IT/Technology, Financial Services, Healthcare, or General).</p>
      </section>
      )}

      {/* FREE VS PAID COMPARISON */}
      {view === 'report' && (
      <section className="content-section alt-bg detail-screen">
        <button className="back-link" onClick={() => setView('tiles')}>← Back to Learn More</button>
        <h2>Free vs. Full Report</h2>
        <div className="comparison-table">
          <div className="comparison-col">
            <h3>Free (3 Questions)</h3>
            <ul>
              <li>A taste of the real scenarios</li>
              <li>Instant, email-verified only</li>
              <li>Shows you the style of insight</li>
              <li className="dim">Does not reveal your full CHART profile</li>
            </ul>
            <button className="btn-secondary" onClick={onStartTrial}>Try Free →</button>
          </div>
          <div className="comparison-col highlight">
            <h3>Paid — ₹999 Launch Price</h3>
            <ul>
              <li>All 20 questions across all 5 dimensions</li>
              <li>Tailored to your career stage + industry</li>
              <li>Your complete CHART profile — strengths, gaps, specific insight</li>
              <li>The report you can actually act on</li>
            </ul>

            {/* Sample score mockup - placeholder data, purely illustrative */}
            <div className="sample-score-mockup">
              <div className="mockup-label">Sample: what your report looks like</div>
              {[
                { label: 'Trust', score: 3.5 },
                { label: 'Relationship Intelligence', score: 3 },
                { label: 'Human Judgment', score: 2.5 },
                { label: 'Adaptability', score: 2 },
                { label: 'Creativity', score: 1.5 }
              ].map(row => (
                <div key={row.label} className="mockup-bar-row">
                  <span className="mockup-bar-label">{row.label}</span>
                  <div className="mockup-bar-track">
                    <div className="mockup-bar-fill" style={{ width: `${(row.score / 4) * 100}%` }} />
                  </div>
                  <span className="mockup-bar-score">{row.score}</span>
                </div>
              ))}
            </div>

            {/* Fuller sample section - real content structure, illustrative persona */}
            <div className="sample-full-section">
              <div className="sample-full-label">A closer look at one section (illustrative example)</div>
              <div className="sample-persona">Sample: Manager, IT/Technology — Adaptability score 2 / 4</div>
              <div className="sample-tier-tag">Worth deliberate practice</div>
              <p className="sample-rationale">You're capable of adapting, but tend to wait for full clarity before moving. In fast-changing environments, waiting too long often costs more than acting on partial information.</p>
              <div className="sample-action">
                <span className="sample-action-timeline">Build this over the next 2–4 weeks</span>
                <p>Over the next 2–4 weeks, practice acting on partial information at least once, rather than waiting for certainty.</p>
              </div>
              <div className="sample-action">
                <span className="sample-action-timeline">Build this over the next 2–4 weeks</span>
                <p>When a new tool or process is introduced, spend focused time learning it early, rather than waiting until it's required.</p>
              </div>
              <div className="sample-path-preview">
                <strong>First 30 days:</strong> Spend the next 30 days working specifically on Adaptability. Pick one or two of the actions above and practice them in real situations at work.
              </div>
              <p className="sample-footnote">Your real report covers all 5 dimensions this way — plus a full 30/60/90/180-day path, a summary of your top priorities, and a personal note.</p>
            </div>

            <button className="btn-primary" onClick={onStartPaid}>Get Full Report →</button>
          </div>
        </div>
      </section>
      )}

      {/* UNDERSTANDING THE GCC OPPORTUNITY */}
      {view === 'gcc' && (
      <section className="content-section alt-bg detail-screen">
        <button className="back-link" onClick={() => setView('tiles')}>← Back to Learn More</button>
        <h2>Understanding the GCC Opportunity</h2>
        <p className="section-lead">Why this matters now, and why it applies to more people than you might think.</p>

        <div className="gcc-opportunity-list">
          <details className="gcc-opportunity-item">
            <summary>What is a GCC, and why is this suddenly such a big deal?</summary>
            <div className="gcc-opportunity-answer">
              <p>For three decades, India built its reputation on IT Services and Outsourcing — the world's back office for technology and business processes. That story created millions of careers.</p>
              <p>But a new chapter has begun. Global Capability Centres (GCCs) are centres that global companies set up in India — not just to save costs, but to actually build and own critical technology, products, and business capability. The shift has moved through real stages: from outsourcing, to shared services, to owned in-house teams, to today's GCCs — strategic hubs driving innovation, not just delivery.</p>
              <p>This isn't a trend. It's the next real chapter of India's professional story.</p>
            </div>
          </details>

          <details className="gcc-opportunity-item">
            <summary>How big is this, really?</summary>
            <div className="gcc-opportunity-answer">
              <p>India is already home to more than 2,100 Global Capability Centres, generating close to $100 billion in annual revenue — and this is backed at the highest levels of government, not just industry hype.</p>
              <p>At the Mindmine Summit, India's Finance Minister described the country's GCC trajectory as one of the most consequential economic and job-generating opportunities of the coming decade. States are actively competing for this growth — Uttar Pradesh's Chief Minister recently announced a target of 500 new GCC units by 2031, with 40 million square feet of office space planned to support it.</p>
              <p>When both national and state governments are actively building toward this, it's worth paying attention.</p>
            </div>
          </details>

          <details className="gcc-opportunity-item">
            <summary>Is this just happening in Bangalore and Hyderabad?</summary>
            <div className="gcc-opportunity-answer">
              <p>Not anymore. While Bengaluru, Hyderabad, Delhi NCR, Pune, Mumbai, and Chennai remain the largest hubs, GCC growth is actively spreading into emerging cities — Chandigarh, Jaipur, Ahmedabad, Indore, Bhopal, Coimbatore, Kochi, and more.</p>
              <p>This shift matters because it means the opportunity isn't limited to a handful of metros or to people willing to relocate to one specific city. GCCs are becoming a nationwide opportunity, not a regional one.</p>
            </div>
          </details>

          <details className="gcc-opportunity-item">
            <summary>Do I need to be in tech to work in a GCC?</summary>
            <div className="gcc-opportunity-answer">
              <p>No — and this is one of the most common misunderstandings about GCCs. Modern GCCs are enterprise hubs that power the entire business, not just technology delivery. Real, growing opportunities exist across Finance, HR, Legal, Supply Chain, Marketing, Procurement, Risk &amp; Compliance, Cybersecurity, Product, Operations, and Analytics — not only software engineering.</p>
              <p>GCCs aren't hiring only coders. They're hiring problem-solvers, across every function.</p>
            </div>
          </details>

          <details className="gcc-opportunity-item">
            <summary>What kinds of roles are actually emerging?</summary>
            <div className="gcc-opportunity-answer">
              <p>Beyond the well-known technology roles, GCCs are actively building capability in areas like AI governance and compliance, human-AI collaboration design, digital twin engineering, sustainability and ESG reporting, workforce planning and skills strategy, and data-driven decision-making across every business function.</p>
              <p>The common thread: these aren't just technical roles. They combine domain expertise, judgment, and the ability to work across teams and technologies — exactly the kind of capability that's harder to automate, and harder to fake.</p>
            </div>
          </details>

          <details className="gcc-opportunity-item">
            <summary>So what's actually going to separate people who thrive from people who don't?</summary>
            <div className="gcc-opportunity-answer">
              <p>Here's the real question — and it's not the one most people are asking. The question isn't whether GCC jobs will exist. They clearly will, and in large numbers. The real question is whether your skills and behaviors are relevant to what modern GCCs actually need.</p>
              <p>The future won't be confined to any single profession. It will belong to those who adapt and evolve — the ones who understand not just what they know, but how they think, decide, and work under pressure.</p>
              <p><strong>That's exactly what CHART Rubric measures.</strong></p>
            </div>
          </details>
        </div>
      </section>
      )}

      <footer className="landing-footer">
        <div className="footer-logos">
          <img src="/images/sarthi-edge-logo.png" alt="Sarthi Edge" className="footer-logo-primary" />
          <img src="/images/astc-logo.png" alt="ASTC" className="footer-logo-secondary" />
        </div>
        <p className="footer-brand-line">Sarthi Edge, part of ASTC — Agentic SmarTalent Consulting</p>
        <p>CHART Rubric — A rigorous behavioral assessment and development plan for GCC aspirants and professionals.</p>
        <div className="footer-links">
          <button className="footer-link" onClick={onShowContact}>Contact Us</button>
          <span className="footer-dot">·</span>
          <button className="footer-link" onClick={onShowPrivacy}>Privacy Policy</button>
          <span className="footer-dot">·</span>
          <button className="footer-link" onClick={onShowTerms}>Terms & Conditions</button>
          <span className="footer-dot">·</span>
          <button className="footer-link" onClick={onShowRefund}>Refund Policy</button>
        </div>
      </footer>

    </div>
  );
}

export default Landing;
